import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import type { DSColors } from '../../context/ThemeContext';

const CATEGORY_LABEL: Record<string, string> = {
  feature: 'feature',
  improvement: 'improvement',
  bugfix: 'bugfix',
  security: 'security',
  breaking: 'breaking',
};

// Breaking retains its existing category violet without changing the shared palette.
const BREAKING_VIOLET = '#8b5cf6';
const categoryColor = (category: string, dsColors: DSColors, fallback: string) => {
  switch (category) {
    case 'feature': return dsColors.successStrong;
    case 'improvement': return dsColors.signalBlue;
    case 'bugfix': return dsColors.warningStrong;
    case 'security': return dsColors.error;
    case 'breaking': return BREAKING_VIOLET;
    default: return fallback;
  }
};

const CategoryGlyph: React.FC<{ category: string }> = ({ category }) => {
  const common = { strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, strokeWidth: 2 };
  let d: React.ReactNode = null;
  switch (category) {
    case 'feature':
      d = <path {...common} d="M12 4v16m8-8H4" />;
      break;
    case 'improvement':
      d = <path {...common} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />;
      break;
    case 'bugfix':
      d = <path {...common} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />;
      break;
    case 'security':
      d = <path {...common} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />;
      break;
    case 'breaking':
      d = <path {...common} d="M13 10V3L4 14h7v7l9-11h-7z" />;
      break;
    default:
      return null;
  }
  return (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {d}
    </svg>
  );
};

const StatusCategoryMarker: React.FC<{ category: string }> = ({ category }) => {
  const { colors, dsColors } = useTheme();
  const accent = categoryColor(category, dsColors, colors.textSecondary);
  return (
    <span
      className="status-badge status-category"
      style={{
        color: colors.textPrimary,
        borderColor: accent,
        backgroundColor: `${accent}1a`,
      }}
    >
      <span className="status-category-glyph" style={{ color: accent }} aria-hidden="true">
        <CategoryGlyph category={category} />
      </span>
      {CATEGORY_LABEL[category] ?? category}
    </span>
  );
};

export default StatusCategoryMarker;
